import React from 'react';
import { getPagingHref } from './helpers';

const usePaging = ({
	currentPage,
	totalPages,
	isShallow,
	baseHref,
	onPageSwitchClick,
}) => {
	const commonLinkProps = React.useMemo(() => {
		return {
			shallow: isShallow,
			scroll: true,
		};
	}, [isShallow]);

	const getOnLinkClick = React.useCallback(
		(pageNum) => {
			return onPageSwitchClick
				? () => {
						if (onPageSwitchClick)
							onPageSwitchClick(currentPage, pageNum);
				  }
				: undefined;
		},
		[onPageSwitchClick, currentPage],
	);

	const buildPagingHref = React.useCallback(
		(pageNum) => {
			return baseHref ? getPagingHref(baseHref, pageNum) : undefined;
		},
		[baseHref],
	);

	const switches = React.useMemo(
		(sideNum = 1) => {
			const arr = [];
			const leftArr = [];
			const rightArr = [];

			const switchesNum =
				totalPages - 2 <= sideNum * 2
					? totalPages - 2
					: sideNum * 2 + 1;

			for (let i = 0; i < switchesNum; i += 1) {
				if (currentPage - (sideNum * 2 + 2) <= 0) {
					arr[i] = i + 2;
				} else if (currentPage === totalPages) {
					arr[i] = i + currentPage - 3;
				} else if (currentPage + 2 * 2 > totalPages) {
					arr[i] = i + totalPages - 3;
				} else {
					arr[i] = i + currentPage - sideNum;
				}
			}

			const leftSwitchesNum = arr[0] - sideNum - 1;

			for (let i = 0; i < leftSwitchesNum; i += 1) {
				leftArr[i] = i + 2;
			}

			const rightSwitchesNum = totalPages - arr[arr.length - 1] - 1;

			for (let i = 0; i < rightSwitchesNum; i += 1) {
				rightArr[i] = totalPages - 1 - i;
			}

			return {
				arr,
				leftArr,
				rightArr: rightArr.sort((a, b) => a - b),
			};
		},
		[currentPage, totalPages],
	);

	const pages = getPagesList({ currentPage, totalPages });

	const mappedPages = pages.map((p, i) => {
		if (p !== 'ELLIPSIS') return p;
		const prev = pages[i - 1];
		const next = pages[i + 1];
		if (prev + 2 === next) {
			return prev + 1;
		}
		return p;
	});

	return {
		pages: mappedPages,
		switches,
		commonLinkProps,
		getOnLinkClick,
		buildPagingHref,
	};
};

function getPagesList({ currentPage, totalPages }) {
	// reference https://codesandbox.io/s/wwq489j46l?file=/src/components/Pagination.js:89-142
	const range = (from, to, step = 1) => {
		let i = from;
		const rangeArr = [];

		while (i <= to) {
			rangeArr.push(i);
			i += step;
		}

		return rangeArr;
	};

	const generatePages = ({ current, total, pageNeighbours = 1 } = {}) => {
		const totalNumbers = pageNeighbours * 2 + 3;
		const totalBlocks = totalNumbers + 2;

		if (total > totalBlocks) {
			let pages = [];

			const leftBound = current - pageNeighbours;
			const rightBound = current + pageNeighbours;
			const beforeLastPage = total - 1;

			const startPage = leftBound > 2 ? leftBound : 2;
			const endPage =
				rightBound < beforeLastPage ? rightBound : beforeLastPage;

			pages = range(startPage, endPage);

			const pagesCount = pages.length;
			const singleSpillOffset = totalNumbers - pagesCount - 1;

			const leftSpill = startPage > 2;
			const rightSpill = endPage < beforeLastPage;

			const leftSpillPage = 'ELLIPSIS';
			const rightSpillPage = 'ELLIPSIS';

			if (leftSpill && !rightSpill) {
				const extraPages = range(
					startPage - singleSpillOffset,
					startPage - 1,
				);
				pages = [leftSpillPage, ...extraPages, ...pages];
			} else if (!leftSpill && rightSpill) {
				const extraPages = range(
					endPage + 1,
					endPage + singleSpillOffset,
				);
				pages = [...pages, ...extraPages, rightSpillPage];
			} else if (leftSpill && rightSpill) {
				pages = [leftSpillPage, ...pages, rightSpillPage];
			}

			return [1, ...pages, total];
		}

		return range(1, total);
	};

	return generatePages({ current: currentPage, total: totalPages });
}

export default usePaging;
